/**
 * Single source of truth for the PII / sensitive-data redaction layer.
 * Centralized so the deep redactor (`redact.ts`) and the
 * call-site URL/referer sanitizers (`sanitizeUrl.ts`) cannot drift apart.
 *
 * The lists are intentionally conservative to satisfy the ticket's
 * "no false positives" acceptance criterion: only values that are
 * unambiguously sensitive (emails, and values keyed by a known-sensitive
 * name) are scrubbed. Generic identifiers such as `errorCode`,
 * `statusCode`, or `cacheKey` are deliberately left untouched.
 */

/** Placeholder substituted in place of any redacted value. */
export const REDACTED = '[REDACTED]';

/**
 * Matches email addresses anywhere in a string. Used with `String.prototype.replace`
 * (global flag), which is stateless across calls, so a single shared instance is safe.
 *
 * The domain is written as one-or-more dot-terminated labels followed by a
 * letter-only TLD (`(?:[A-Z0-9-]+\.)+[A-Z]{2,}`), where the label class
 * deliberately excludes `.`. This keeps the pattern unambiguous and
 * linear-time — avoiding the polynomial backtracking (ReDoS) of the classic
 * `[A-Z0-9.-]+\.[A-Z]{2,}` form, which overlaps `.` between the character class
 * and the literal dot. Requiring a letter TLD also avoids matching
 * `package@1.2.3`-style version strings that appear in stack-trace paths.
 */
export const EMAIL_REGEX = /[A-Z0-9._%+-]+@(?:[A-Z0-9-]+\.)+[A-Z]{2,}/gi;

/**
 * Lower-cased word segments that mark a *context object key* as sensitive.
 * A key matches when its full lower-cased form, or any camelCase / snake_case /
 * kebab-case segment, is in this set (see `isSensitiveKey`). `code` is
 * deliberately excluded here (it would scrub legitimate `errorCode` /
 * `statusCode` / `zipCode` fields); it is only sensitive as a URL query
 * parameter (see `SENSITIVE_QUERY_PARAM_KEYS`).
 */
export const SENSITIVE_KEY_SEGMENTS = new Set<string>([
    'password',
    'passwd',
    'pwd',
    'token',
    'secret',
    'authorization',
    'auth',
    'bearer',
    'session',
    'sessionid',
    'sid',
    'jwt',
    'otp',
    'email',
    'emailaddress',
    'referer',
    'referrer',
    'credential',
    'credentials',
    'cookie',
    'apikey',
    'api_key',
]);

/**
 * Lower-cased URL path segments whose next segment carries a secret (password-reset,
 * password-change, and sign-out tokens; billing tokens). `sanitizeUpstreamUrl` always masks
 * that next segment, however ordinary it looks.
 */
export const SENSITIVE_PATH_PARENT_SEGMENTS = new Set<string>([
    'reset',
    'change',
    'logoutdevices',
    'signoutdevice',
    'billing',
]);

/**
 * Lower-cased URL query-parameter names whose values must be redacted.
 * Includes `code` and `email` (password-reset / email-change links flow
 * through query strings — see the SSR call site) and `key`,
 * which are matched only as exact query-parameter names, never as
 * arbitrary substrings, so benign context keys are unaffected.
 */
export const SENSITIVE_QUERY_PARAM_KEYS = new Set<string>([
    'password',
    'passwd',
    'pwd',
    'token',
    'access_token',
    'accesstoken',
    'refresh_token',
    'refreshtoken',
    'id_token',
    'idtoken',
    'auth',
    'authorization',
    'bearer',
    'secret',
    'session',
    'session_id',
    'sessionid',
    'adsessionid',
    'sid',
    'jwt',
    'otp',
    'code',
    'email',
    'signature',
    'sig',
    'key',
    'apikey',
    'api_key',
    'credential',
    'credentials',
    'userid',
    'ipaddress',
]);
